import { TeamList } from '../components/Teamlist';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

// Card original usado apenas para a Equipe Atual
function TeamMemberCard({ element }) {
  const { t } = useTranslation();

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -5 }}
      className="glass-card-hover p-6 flex flex-col sm:flex-row items-center gap-6"
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        className="relative flex-shrink-0"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full blur-xl" />
        <img
          src={element.img}
          alt={element.name}
          className="relative w-28 h-28 rounded-full object-cover border-2 border-glass-border"
        />
      </motion.div>
      <div className="flex-1 text-center sm:text-left">
        <h3 className="text-xl font-bold text-text-primary mb-1">{element.name}</h3>
        <p className="text-primary font-medium text-sm mb-3">{t(element.post)}</p>
        <p className="text-text-secondary text-sm leading-relaxed mb-4">
          {t(element.description)}
        </p>
        <a
          href={element.curriculum}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors"
        >
          <img src="/img/lattes.svg" alt="Lattes" className="w-5 h-5" />
          <span>{t('ui.lattesProfile')}</span>
        </a>
      </div>
    </motion.div>
  );
}

function Team() {
  const { t } = useTranslation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Filtramos os ex-membros da equipe principal usando palavras-chave
  const alumniKeywords = ['Aluisio', 'Daiane', 'Emanuel', 'Lucas'];
  const currentTeam = TeamList.filter(
    (member) => !alumniKeywords.some((name) => member.name.toLowerCase().includes(name.toLowerCase()))
  );

  // Nova lista estática com os dados exatos do Alumni
  const alumniData = [
    { name: 'Aluisio Alonso Lopes Tomas', period: t('home.alumni-aluisio') },
    { name: 'Daiane Dantas da Silva', period: t('home.alumni-daiane')},
    { name: 'Emanuel Franklyn de Lima Maciel', period: t('home.alumni-emanuel') },
    {name: 'Lucas de sá Batista', period: t('home.Alumni-lucas')}
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="min-h-screen pt-24 md:pt-28 pb-12 px-4 sm:px-6 lg:px-8">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto"
      >
        {/* Header - Equipe Atual */}
        <motion.div variants={cardVariants} className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-6"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('ui.ourTeam')}
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            {t('header.team')}
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {t('ui.teamIntro')}
          </p>
        </motion.div>

        {/* Current Team Grid */}
        <motion.div variants={containerVariants} className="flex flex-col gap-6 mb-20">
          {currentTeam.map((member, index) => (
            <TeamMemberCard key={`current-${index}`} element={member} />
          ))}
        </motion.div>

        {/* Header - Alumni (Ex-membros) */}
        {alumniData.length > 0 && (
          <>
            <motion.div variants={cardVariants} className="text-center mb-10">
              <div className="h-px w-full max-w-md mx-auto bg-gradient-to-r from-transparent via-border-light to-transparent mb-10" />
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                Alumni
              </h2>
              <p className="text-base text-text-secondary max-w-2xl mx-auto">
                {t('home.alumni')}
              </p>
            </motion.div>

            {/* Alumni Grid*/}
            <motion.div 
              variants={containerVariants} 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-90 hover:opacity-100 transition-opacity duration-300"
            >
              {alumniData.map((alumni, index) => (
                <motion.div
                  key={`alumni-${index}`}
                  variants={cardVariants}
                  whileHover={{ y: -3 }}
                  className="glass-card-hover p-5 flex flex-col justify-center items-center text-center border-t-2 border-t-primary/30"
                >
                  <h3 className="text-lg font-bold text-text-primary mb-2">{alumni.name}</h3>
                  <span className="text-text-secondary text-sm bg-primary/10 px-3 py-1 rounded-full">
                    {alumni.period}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {isMobile && showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-4 z-50 w-14 h-14 bg-primary hover:bg-primary/90 active:bg-primary/80 text-white rounded-full shadow-xl flex items-center justify-center transition-colors border-2 border-white/20"
            aria-label={t('ui.scrollToTop')}
            title={t('ui.scrollToTop')}
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </section>
  );
}

export default Team;